import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";

const ROLES = [{v:"admin",l:"Admin"},{v:"finance",l:"Finance"}];

export default function UsersPage() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const empty = {name:"", email:"", no_telp:"", password:"", confirm:"", role:"finance"};
  const [form, setForm] = useState(empty);

  const load = async () => setItems((await api.get("/users")).data);
  useEffect(()=>{ load(); },[]);
  const upd = (k) => (e) => setForm({...form, [k]: e?.target ? e.target.value : e});

  const save = async () => {
    if (form.password !== form.confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    try {
      const { confirm, ...payload } = form;
      await api.post("/users", payload);
      setOpen(false); setForm(empty); load(); toast.success("User ditambahkan ke workspace");
    } catch(e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus user ini?")) return;
    try { await api.delete(`/users/${id}`); load(); toast.success("Terhapus"); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (u) => {
    const s = u.status === "active" ? "inactive" : "active";
    try { await api.put(`/users/${u.id}`, {status: s}); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(items, 10);

  return (<>
    <PageHeader title="Manajemen User Workspace" subtitle={`Workspace: ${me?.workspace_name || me?.workspace_type}`}
      actions={<Button onClick={()=>setOpen(true)} className="brand-btn rounded-sm" data-testid="add-user"><Plus size={14} className="mr-1"/>Tambah User</Button>}/>
    <div className="p-4 md:p-8">
      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
            <th className="p-3">Nama</th><th className="p-3">Email</th><th className="p-3">No. Telp</th><th className="p-3">Peran</th><th className="p-3">Status</th><th className="p-3 text-right">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length===0 && <tr><td colSpan="6" className="text-center py-10 text-slate-400">Belum ada user.</td></tr>}
            {paged.map(u => (
              <tr key={u.id} className="border-b hover:bg-slate-50">
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3 font-mono text-[12px]">{u.no_telp || "-"}</td>
                <td className="p-3"><span className="text-[11px] px-2 py-0.5 brand-soft brand-text rounded-sm">{u.role}</span></td>
                <td className="p-3">
                  <button onClick={()=>toggleStatus(u)}
                    className={`text-[11px] px-2 py-0.5 rounded-sm ${u.status==="inactive"?"bg-rose-100 text-rose-700":"bg-emerald-100 text-emerald-700"}`}>
                    {u.status || "active"}
                  </button>
                </td>
                <td className="p-3 text-right">
                  {u.id !== me?.id && (
                    <button onClick={()=>del(u.id)} className="p-1.5 hover:bg-rose-50 rounded-sm">
                      <Trash2 size={14} className="text-rose-600"/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="users-pagination"/>
      </div>
    </div>

    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle>Tambah User ke Workspace</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama Lengkap</Label><Input value={form.name} onChange={upd("name")}/></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={upd("email")}/></div>
          <div><Label>No. Telp</Label><Input value={form.no_telp} onChange={upd("no_telp")}/></div>
          <div><Label>Peran</Label>
            <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{ROLES.map(r=><SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Password</Label>
            <div className="relative">
              <Input type={show?"text":"password"} value={form.password} onChange={upd("password")}/>
              <button type="button" onClick={()=>setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500">{show?<EyeOff size={16}/>:<Eye size={16}/>}</button>
            </div>
          </div>
          <div><Label>Konfirmasi Password</Label><Input type={show?"text":"password"} value={form.confirm} onChange={upd("confirm")}/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
          <Button onClick={save} className="brand-btn">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}
